import { Box } from '@mui/material';
import { useState, useEffect } from 'react';
import {
  ActivityLog,
  ActivityLogSearchRequest,
  ActivityLogSearchResponse,
  getEmailActivityLog,
  getJobsActivityLogs,
} from 'wisestep-api';
import { WsColors, WsListingLoader, timeCalendarSince } from 'wisestep-base';

import NoTimeLineDataPreview from '../../../features/candidateLayout/components/activityLogs/components/activity/NoTimeLineDataPreview';
import Preview, {
  PreviewProps,
} from '../../../features/candidateLayout/components/activityLogs/components/activity/Preview';
import AdavanceFilters from '../../../features/candidateLayout/components/activityLogs/components/advFilters/AdavanceFilters';
import UpdatedDetailsPreview from '../../../features/candidateLayout/components/activityLogs/components/timeLine/components/UpdatedDetailsPreview';
import TimeLine from '../../../features/candidateLayout/components/activityLogs/components/timeLine/TimeLIne';
import Resizable from '../../../features/candidateLayout/components/childComponets/resizable/Resizable';

interface ActivityLogProps {
  showAdvancedFilters?: boolean;
  activityType?: string;
  jobId?: string;
}

const JobActivityLog = ({
  showAdvancedFilters = true,
  activityType = '',
  jobId = '',
}: ActivityLogProps) => {
  const [dragValue, setDragValue] = useState<boolean>(true);
  const [preview, setPreview] = useState<PreviewProps | null>(null);
  const [searchRequest, setSearchRequest] = useState<ActivityLogSearchRequest>({});
  const [showPreviewDetails, setShowPreviewDetails] = useState<boolean>(false);
  const [dialogAction, setDialogAction] = useState<boolean>(false);
  const [auditId, setAuditId] = useState<string>('');
  const [activityLog, setActivityLogs] = useState<ActivityLogSearchResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setSearchRequest({
      page: 0,
      size: 20,
      jobIds: jobId ? [jobId] : [],
      activityType,
      recruiters: [],
    });
  }, [activityType, jobId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (searchRequest.jobIds?.length || searchRequest.activityType) {
        try {
          const response = await getJobsActivityLogs(searchRequest);
          setActivityLogs(response.data?.content ?? []);
        } catch (err) {
          console.error('Error fetching activity logs:', err);
        }
      }
      setLoading(false);
    })();
  }, [searchRequest]);

  const onSubmit = (data: ActivityLogSearchRequest) => {
    setSearchRequest({
      ...data,
      jobIds: jobId ? [jobId] : [],
      workspaceId: '',
      page: 0,
      size: 20,
    });
  };

  const onReset = () => {
    setSearchRequest({
      jobIds: jobId ? [jobId] : [],
      workspaceId: '',
      page: 0,
      size: 20,
    });
  };

  const openPreview = (action: boolean) => setShowPreviewDetails(action);

  const onClose = (action: boolean) => {
    setShowPreviewDetails(action);
    setDialogAction(true);
  };

  const logId = (id: string) => setAuditId(id);

  const onPreviewClick = async (activity: ActivityLog) => {
    if (activity.activityType === 'EMAIL' || activity.toEmail) {
      const response = await getEmailActivityLog(activity.activityId ?? '');
      const emailLog = response.data;
      setPreview({
        title: `Sent Email By ${activity.recruiterName ?? activity.recruiterId}`,
        date: `${timeCalendarSince(activity.createdOn, true)} to ${emailLog.to}`,
        type: 'EMAIL',
        email: {
          body: emailLog.body,
          subject: emailLog.subject,
          jobTitle: activity.jobTitle ?? '',
          location: null,
        },
      });
    }
  };

  return (
    <Resizable
      leftPanel={
        <>
          {!loading ? (
            <Box bgcolor={WsColors.White}>
              {activityLog.length > 0 ? (
                activityLog.map((activity) => (
                  <TimeLine
                    key={String(activity.date)}
                    onclick={onPreviewClick}
                    activity={activity}
                    openPreview={openPreview}
                    logId={logId}
                    closeDialog={dialogAction}
                    jobsActivity={true}
                  />
                ))
              ) : (
                <NoTimeLineDataPreview title="No activity found" subtitle="" />
              )}
            </Box>
          ) : (
            <WsListingLoader />
          )}
        </>
      }
      rightPanel={
        <>
          {preview ? (
            <Preview
              date={preview.date}
              type={preview.type}
              title={preview.title}
              message={preview.message}
              email={preview.email}
              onClose={() => setPreview(null)}
            />
          ) : showAdvancedFilters && !showPreviewDetails ? (
            <AdavanceFilters
              defaultFilters={searchRequest}
              onApply={onSubmit}
              onReset={onReset}
              forJobs
            />
          ) : (
            <UpdatedDetailsPreview onClose={onClose} auditId={auditId} />
          )}
        </>
      }
      defaultSizes={!showAdvancedFilters ? [100, 0] : [75, 25]}
      minSize="45%"
      maxSize="80%"
      isLoading={loading}
      pseudoDragValue={dragValue}
      setPseudoDragValue={setDragValue}
    />
  );
};

export default JobActivityLog;
